import React from "react";
import PropTypes from "prop-types";
import CustomImage from "../../atoms/customImage";
import { getProductDeliveryDisplay } from "../../../utils/plp/serviceabilityDelivery";

const TIME_TEXT_CLASS_NAME =
  "font-inter text-[11px] md:text-12 font-600 leading-16 text-[#47483C] [font-variant-numeric:lining-nums_proportional-nums] [font-feature-settings:'dlig'_on]";

/**
 * Renders product delivery timing with iconography based on serviceability response.
 *
 * @param {Object} props
 * @param {string|Object} [props.deliveryEntry] - Serviceability entry or legacy string
 * @param {boolean} [props.isDeliveryTimeHidden] - Day/date only, no delivery time (deliveryTimeVisibility A/B).
 * @returns {JSX.Element|null}
 */
const EarliestDeliveryDate = ({ deliveryEntry, isDeliveryTimeHidden = false }) => {
  const display = getProductDeliveryDisplay(deliveryEntry, { isTimeHidden: isDeliveryTimeHidden });

  if (!display) {
    return null;
  }

  if (display.type === "legacy") {
    return (
      <div className="text-xs">
        <span className={TIME_TEXT_CLASS_NAME}>Earliest Delivery : {display.text}</span>
      </div>
    );
  }

  if (display?.type === "instant" || display?.type === "today" || display?.type === "morning") {
    return (
      <div className="flex w-full min-w-0 items-center justify-between gap-8">
        {display?.primaryIcon && (
          <CustomImage
            src={display.primaryIcon}
            alt=""
            width={display.primaryIconWidth}
            height={display.primaryIconHeight}
            sizes="80px"
            isPriority={false}
            aria-hidden={true}
          />
        )}
        {display?.text && (
          <div className="flex shrink-0 items-center gap-4">
            {display?.secondaryIcon && (
              <CustomImage
                src={display.secondaryIcon}
                alt=""
                width={display.secondaryIconWidth}
                height={display.secondaryIconHeight}
                sizes="20px"
                isPriority={false}
                aria-hidden={true}
              />
            )}
            <span className={TIME_TEXT_CLASS_NAME}>{display?.text}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 items-center gap-4">
      {display?.primaryIcon && (
        <CustomImage
          src={display.primaryIcon}
          alt=""
          width={display.primaryIconWidth}
          height={display.primaryIconHeight}
          sizes="80px"
          isPriority={false}
          aria-hidden={true}
        />
      )}
      {display?.text && display?.secondaryIcon && (
        <CustomImage
          src={display.secondaryIcon}
          alt=""
          width={display.secondaryIconWidth}
          height={display.secondaryIconHeight}
          sizes="20px"
          isPriority={false}
          aria-hidden={true}
        />
      )}
      {display?.text && <span className={TIME_TEXT_CLASS_NAME}>{display?.text}</span>}
    </div>
  );
};

EarliestDeliveryDate.propTypes = {
  deliveryEntry: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isDeliveryTimeHidden: PropTypes.bool,
};

export default React.memo(EarliestDeliveryDate);
